import { useLayoutEffect, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react';
import { Alert, Button, Form, Input } from 'antd';
import type { GetRef } from 'antd';
import { EditOutlined, MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { AUTH_FORM_PROPS, authStyles } from 'layouts/auth';
import {
  isCodeWrong,
  mapRegisterError,
  mapVerifyError,
  registerAccount,
  REGISTER_MESSAGES,
  verifyAccount,
} from './register-api';
import type { RegisterPayload } from './register-api';
import { EmailTakenAlert, isWideScreen, useSecondsLeft } from './register-parts';
import styles from './register.module.scss';

type OtpRef = GetRef<typeof Input.OTP>;

/** Máy chủ sinh mã 4 chữ số (user.service.ts: 1000 + random*9000), app cũng nhập 4 ô. */
export const CODE_LENGTH = 4;
const RESEND_COOLDOWN_MS = 60_000;
const CODE_INPUT_ID = 'register-verify_code';

interface VerifyStepProps {
  email: string;
  /** Dữ liệu đã gửi ở bước 1 — "Gửi lại mã" gửi lại đúng dữ liệu này (chỉ nằm trong bộ nhớ). */
  payload: RegisterPayload;
  onVerified: () => void;
  /** Quay về bước 1, giữ nguyên các ô đã điền. */
  onEdit: () => void;
}

type Notice =
  | { kind: 'emailTaken' }
  | { kind: 'alert'; type: 'error' | 'success'; message: ReactNode };

const onlyDigits = (value: string) => value.replace(/\D/g, '');

/**
 * Bước 2/3: nhập mã 4 số (antd Input.OTP, 4 ô như app) → POST users/verify.
 * Đủ 4 số thì tự gửi; Enter hoặc nút "Xác nhận" cũng gửi.
 */
const VerifyStep = ({ email, payload, onVerified, onEdit }: VerifyStepProps) => {
  const otpRef = useRef<OtpRef>(null);
  // Đổi key = dựng lại 4 ô (xoá mã sai, hoặc điền mã dán từ một đoạn chữ).
  const [otp, setOtp] = useState({ key: 0, initial: '' });
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendAt, setResendAt] = useState(() => Date.now() + RESEND_COOLDOWN_MS);
  const secondsLeft = useSecondsLeft(resendAt);
  const busy = useRef(false);

  const otpInputs = () =>
    Array.from(otpRef.current?.nativeElement?.querySelectorAll('input') || []);

  /** Con trỏ vào ô trống đầu tiên (đủ 4 số thì ô cuối). */
  const focusFirstEmpty = () => {
    const inputs = otpInputs();
    (inputs.find((input) => !input.value) || inputs[inputs.length - 1])?.focus();
  };

  // Input.OTP 5.29 gắn aria-label tiếng Anh "OTP Input n" và không nhận autoComplete cho từng ô.
  // Sửa thẳng trên DOM sau khi dựng: React không ghi lại vì các prop này không đổi giữa các lần render.
  useLayoutEffect(() => {
    const inputs = otpInputs();
    inputs.forEach((input, index) => {
      input.setAttribute('aria-label', `Chữ số ${index + 1} trên ${CODE_LENGTH}`);
      input.setAttribute('autocomplete', index === 0 ? 'one-time-code' : 'off');
      input.setAttribute('pattern', '[0-9]*');
      if (index === 0) input.id = CODE_INPUT_ID;
    });
    // Con trỏ vào ô trống đầu tiên (ô 1 khi mới vào bước / vừa xoá mã sai). Lúc mới vào bước chỉ
    // làm vậy trên màn rộng, như mọi bước khác (điện thoại sẽ bật bàn phím che nửa trang); sau khi
    // xoá mã sai, gửi lại mã hay dán mã (key > 0) thì người dùng đang nhập mã nên luôn đưa về ô.
    if (otp.key > 0 || isWideScreen()) {
      inputs[Math.min(otp.initial.length, CODE_LENGTH - 1)]?.focus();
    }
    // otpInputs chỉ đọc ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const resetCode = (initial = '') => {
    setCode(initial);
    setOtp((prev) => ({ key: prev.key + 1, initial }));
  };

  const submit = async (value: string) => {
    if (busy.current) return;
    setNotice(null);
    if (!value) {
      setCodeError(REGISTER_MESSAGES.codeRequired);
      focusFirstEmpty();
      return;
    }
    if (!/^\d{4}$/.test(value)) {
      setCodeError(REGISTER_MESSAGES.codeFormat);
      focusFirstEmpty();
      return;
    }
    busy.current = true;
    setVerifying(true);
    setCodeError(null);
    try {
      await verifyAccount(email, value);
      onVerified();
    } catch (error) {
      if (isCodeWrong(error)) {
        setCodeError(REGISTER_MESSAGES.codeWrong);
        resetCode();
      } else {
        setNotice({ kind: 'alert', type: 'error', message: mapVerifyError(error) });
      }
    } finally {
      busy.current = false;
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (busy.current || secondsLeft > 0) return;
    busy.current = true;
    setResending(true);
    setNotice(null);
    try {
      await registerAccount(payload);
      setResendAt(Date.now() + RESEND_COOLDOWN_MS);
      setCodeError(null);
      resetCode();
      setNotice({
        kind: 'alert',
        type: 'success',
        message: (
          <>
            Đã gửi mã mới tới <strong className={styles.strong}>{email}</strong>. Chỉ mã trong email
            mới nhất còn dùng được.
          </>
        ),
      });
    } catch (error) {
      const result = mapRegisterError(error);
      if (result.kind === 'emailTaken') setNotice({ kind: 'emailTaken' });
      else if (result.kind === 'field') {
        setNotice({ kind: 'alert', type: 'error', message: REGISTER_MESSAGES.tiktokTakenOnResend });
      } else setNotice({ kind: 'alert', type: 'error', message: result.message });
    } finally {
      busy.current = false;
      setResending(false);
    }
  };

  // Chặn phím chữ / ký hiệu ngay từ đầu: nếu để Input.OTP nhận rồi mới lọc (formatter), các số
  // phía sau sẽ bị dồn sang trái. formatter bên dưới chỉ còn là lưới an toàn (bàn phím ảo, IME).
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key.length === 1 && !/\d/.test(event.key)) event.preventDefault();
  };

  // Tự xử lý mọi lần dán: Input.OTP cắt chuỗi dán theo số ô TRƯỚC khi lọc, nên dán
  // "Mã của bạn: 1234" hoặc " 1234 " sẽ mất mã. Ở đây lấy đúng các chữ số rồi điền từ ô 1.
  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    const digits = onlyDigits(event.clipboardData.getData('text')).slice(0, CODE_LENGTH);
    event.preventDefault();
    if (!digits) return;
    setCodeError(null);
    resetCode(digits);
    if (digits.length === CODE_LENGTH) submit(digits);
  };

  return (
    <Form {...AUTH_FORM_PROPS} name="register-verify" onFinish={() => submit(code)}>
      {notice?.kind === 'emailTaken' && <EmailTakenAlert email={email} />}
      {notice?.kind === 'alert' && (
        <Alert
          type={notice.type}
          showIcon
          className={authStyles.formAlert}
          message={notice.message}
        />
      )}

      <div className={styles.mailCard}>
        <span className={styles.mailIcon} aria-hidden="true">
          <MailOutlined />
        </span>
        <p className={styles.mailText}>
          <span className={styles.mailCaption}>Mã gồm {CODE_LENGTH} chữ số đã gửi tới</span>
          <span className={styles.mailAddress}>{email}</span>
        </p>
      </div>

      <Form.Item
        label={`Mã xác nhận (${CODE_LENGTH} số)`}
        htmlFor={CODE_INPUT_ID}
        validateStatus={codeError ? 'error' : undefined}
        help={codeError || undefined}
        className={styles.codeItem}
      >
        <div className={styles.otp} onPasteCapture={onPaste} onKeyDownCapture={onKeyDown}>
          <Input.OTP
            key={otp.key}
            ref={otpRef}
            length={CODE_LENGTH}
            defaultValue={otp.initial}
            inputMode="numeric"
            aria-label={`Mã xác nhận ${CODE_LENGTH} số`}
            formatter={(value) => value.replace(/[^\d ]/g, '')}
            onInput={(cells) => {
              setCode(cells.join(''));
              if (codeError) setCodeError(null);
              if (notice?.kind === 'alert' && notice.type === 'error') setNotice(null);
            }}
            // antd chỉ gọi onChange khi đủ 4 ô và mã khác lần trước → tự gửi.
            onChange={(value) => submit(value)}
          />
        </div>
      </Form.Item>

      <Button type="primary" htmlType="submit" size="large" block loading={verifying}>
        {verifying ? 'Đang kiểm tra…' : 'Xác nhận'}
      </Button>

      <div className={styles.codeActions}>
        <Button
          type="link"
          className={styles.linkButton}
          icon={<EditOutlined aria-hidden="true" />}
          onClick={onEdit}
          disabled={verifying || resending}
        >
          Sửa thông tin
        </Button>
        <Button
          type="link"
          className={styles.linkButton}
          icon={<ReloadOutlined aria-hidden="true" />}
          onClick={resend}
          loading={resending}
          disabled={verifying || secondsLeft > 0}
        >
          {resending
            ? 'Đang gửi lại…'
            : secondsLeft > 0
            ? `Gửi lại mã sau ${secondsLeft} giây`
            : 'Gửi lại mã'}
        </Button>
      </div>

      <p className={`${authStyles.hint} ${styles.help}`}>
        Không thấy mã? Kiểm tra thư mục Spam hoặc Quảng cáo, rồi thử Gửi lại mã.
      </p>
    </Form>
  );
};

export default VerifyStep;
